import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TimesheetFilters = memo(({ 
  dateRange,
  onDateRangeChange,
  statusFilter, 
  onStatusFilterChange,
  contractFilter,
  onContractFilterChange,
  contracts = [],
  onExportClick,
  isExporting = false,
  canExport = true
}) => {
  const [startDate, setStartDate] = useState(dateRange?.start || '');
  const [endDate, setEndDate] = useState(dateRange?.end || '');

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  // Memoize contract options to prevent unnecessary recreations
  const contractOptions = useMemo(() => [
    { value: 'all', label: 'All Contracts' },
    ...contracts.map(c => ({
      value: c.id,
      label: `${c.jobs?.title || 'Contract'} - ${c.employer?.company_name || c.employer?.full_name || 'Unknown'}`
    }))
  ], [contracts]);

  const today = new Date().toISOString().split('T')[0];

  // Sync local state when props change
  useEffect(() => {
    if (dateRange) {
      setStartDate(dateRange.start || '');
      setEndDate(dateRange.end || '');
    }
  }, [dateRange]);

  const handleStartDateChange = useCallback((e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    
    // If end date is before start date, adjust it
    if (endDate && newStartDate > endDate) {
      setEndDate(newStartDate);
      if (onDateRangeChange) {
        onDateRangeChange({ start: newStartDate, end: newStartDate });
      }
    } else {
      if (onDateRangeChange) {
        onDateRangeChange({ start: newStartDate, end: endDate });
      }
    }
  }, [endDate, onDateRangeChange]);

  const handleEndDateChange = useCallback((e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    
    // If start date is after end date, adjust it
    if (startDate && newEndDate < startDate) {
      setStartDate(newEndDate);
      if (onDateRangeChange) {
        onDateRangeChange({ start: newEndDate, end: newEndDate });
      }
    } else {
      if (onDateRangeChange) {
        onDateRangeChange({ start: startDate, end: newEndDate });
      }
    }
  }, [startDate, onDateRangeChange]);

  const handleClearDates = useCallback(() => {
    setStartDate('');
    setEndDate('');
    if (onDateRangeChange) {
      onDateRangeChange({ start: null, end: null });
    }
  }, [onDateRangeChange]);

  const handleTodayClick = useCallback(() => {
    setStartDate(today);
    setEndDate(today);
    if (onDateRangeChange) {
      onDateRangeChange({ start: today, end: today });
    }
  }, [today, onDateRangeChange]);

  const handleThisMonthClick = useCallback(() => {
    const firstDay = new Date(today.split('-')[0], parseInt(today.split('-')[1]) - 1, 1)
      .toISOString().split('T')[0];
    setStartDate(firstDay);
    setEndDate(today);
    if (onDateRangeChange) {
      onDateRangeChange({ start: firstDay, end: today });
    }
  }, [today, onDateRangeChange]);

  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRangeDescription = () => {
    if (!startDate && !endDate) return 'All Time';
    if (startDate === endDate) return `${getFormattedDate(startDate)}`;
    if (startDate && endDate) return `${getFormattedDate(startDate)} - ${getFormattedDate(endDate)}`;
    if (startDate) return `From ${getFormattedDate(startDate)}`;
    return `Until ${getFormattedDate(endDate)}`;
  };

  const handleStatusChange = useCallback((e) => {
    const value = e.target.value;
    if (value !== undefined && onStatusFilterChange) {
      onStatusFilterChange(value);
    }
  }, [onStatusFilterChange]);

  const handleContractChange = useCallback((e) => {
    const value = e.target.value;
    if (value !== undefined && onContractFilterChange) {
      onContractFilterChange(value);
    }
  }, [onContractFilterChange]);

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="space-y-4">
        {/* Date Range Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <div className="space-y-2 mb-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  max={today}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Start date"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  max={today}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="End date"
                />
              </div>
            </div>
            <div className="text-xs text-gray-600 px-3 py-2 bg-gray-50 rounded-lg">
              {getRangeDescription()}
            </div>
          </div>

          {/* Quick Date Buttons */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={handleTodayClick}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex-1"
            >
              Today
            </button>
            <button
              onClick={handleThisMonthClick}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex-1"
            >
              This Month
            </button>
            <button
              onClick={handleClearDates}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Clear dates"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Filters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contract
            </label>
            <select
              value={contractFilter}
              onChange={handleContractChange}
              disabled={contracts.length === 0}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {contractOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Export Button */}
      {onExportClick && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={onExportClick}
            disabled={isExporting || !canExport}
            className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19m0 0l-7-7m7 7l7-7m0-5V5a2 2 0 00-2-2H5a2 2 0 00-2 2v9" />
            </svg>
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if actual filter values or contracts changed
  return (
    prevProps.dateRange?.start === nextProps.dateRange?.start &&
    prevProps.dateRange?.end === nextProps.dateRange?.end &&
    prevProps.statusFilter === nextProps.statusFilter &&
    prevProps.contractFilter === nextProps.contractFilter &&
    prevProps.contracts?.length === nextProps.contracts?.length &&
    prevProps.contracts?.every((c, i) => c.id === nextProps.contracts?.[i]?.id) &&
    prevProps.onDateRangeChange === nextProps.onDateRangeChange &&
    prevProps.isExporting === nextProps.isExporting &&
    prevProps.canExport === nextProps.canExport
  );
});

TimesheetFilters.displayName = 'TimesheetFilters';

export default TimesheetFilters;
